/**
 * PlayerLevelDependencyProvider — computes a level's asset closure for the player runtime.
 *
 * Primary source is the build-baked `manifest.levelDependencies[levelGuid]` (full transitive
 * closure computed with the editor's dependency graph). When a deserialized level is available
 * we also union the DIRECT component refs (runtime fallback — covers a missing/partial bake and
 * runtime-spawned content), via the shared core helper.
 */

import {
  type LevelDependencyProvider,
  type SerializedLevel,
  extractAssetRefsFromSerializedLevel,
} from "@engine/core";
import type { AssetSubsystem } from "../subsystems/AssetSubsystem";

export class PlayerLevelDependencyProvider implements LevelDependencyProvider {
  constructor(private readonly assets: AssetSubsystem) {}

  async getLevelClosure(levelGuid: string, serialized?: SerializedLevel): Promise<Set<string>> {
    const closure = new Set<string>();

    const baked = this.assets.getManifest()?.levelDependencies?.[levelGuid];
    if (baked) {
      for (const g of baked) closure.add(g);
    }

    if (serialized) {
      for (const g of extractAssetRefsFromSerializedLevel(serialized)) closure.add(g);
    }

    return closure;
  }
}
